import React, { useState, useEffect, useRef } from "react";
import { ListPageSkeleton } from "../../Components/Skeleton";
import SearchBar from "../../Components/SearchBar";
import Button from "../../Components/Button";
import { IoMdAdd, IoMdClose, IoMdSearch } from "react-icons/io";
import { Modal } from "../../Components/Modal";
import { supabase } from "../../supabaseClient";
import {
  MdModeEditOutline,
  MdDelete,
  MdContentCopy,
  MdCheckCircle,
} from "react-icons/md";
import { FiGlobe, FiCode, FiDatabase } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { motion, AnimatePresence } from "framer-motion";

function Languages() {
  const [showModal, setShowModal] = useState(false);
  const [languageName, setLanguageName] = useState("");
  const [loading, setLoading] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editLanguage, setEditLanguage] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const debouncedSearch = useDebounce(searchTerm, 400);

  const PAGE_SIZE = 50;
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const containerRef = useRef(null);

  // Debounced search hook
  function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  }

  const fetchLanguages = async (pageToFetch = 0, isNew = false) => {
    if (loadingMore) return;
    setLoadingMore(true);

    let query = supabase
      .from("languages")
      .select("*")
      .range(pageToFetch * PAGE_SIZE, (pageToFetch + 1) * PAGE_SIZE - 1);

    if (debouncedSearch.trim() !== "") {
      query = query.ilike("languages", `%${debouncedSearch}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching languages:", error);
    } else {
      if (isNew) {
        setLanguages(data);
      } else {
        setLanguages((prev) => [...prev, ...data]);
      }
      if (data.length < PAGE_SIZE) {
        setHasMore(false);
      }
    }

    setLoadingMore(false);
    setIsLoading(false);
  };

  const handleAddOrUpdateLanguage = async () => {
    if (!languageName.trim()) {
      Swal.fire({
        position: "top-end",
        icon: "warning",
        title: "Please enter a language name",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        toast: true,
        background: "white",
        customClass: {
          popup: "shadow-xl rounded-xl p-3",
        },
      });
      return;
    }
    setLoading(true);
    try {
      if (editLanguage) {
        const { error } = await supabase
          .from("languages")
          .update({ languages: languageName.trim() })
          .eq("id", editLanguage.id);

        if (error) {
          Swal.fire({
            position: "top-end",
            icon: "error",
            title: "Failed to update language",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            toast: true,
            background: "white",
            customClass: {
              popup: "shadow-xl rounded-xl p-3",
            },
          });
        } else {
          Swal.fire({
            position: "top-end",
            icon: "success",
            title: "Language updated successfully",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            toast: true,
            background: "white",
            customClass: {
              popup: "shadow-xl rounded-xl p-3",
            },
          });
        }
      } else {
        const { error } = await supabase
          .from("languages")
          .insert([{ languages: languageName.trim() }]);
        if (error) {
          Swal.fire({
            position: "top-end",
            icon: "error",
            title: "Failed to add language",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            toast: true,
            background: "white",
            customClass: {
              popup: "shadow-xl rounded-xl p-3",
            },
          });
        } else {
          Swal.fire({
            position: "top-end",
            icon: "success",
            title: "Language added successfully",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            toast: true,
            background: "white",
            customClass: {
              popup: "shadow-xl rounded-xl p-3",
            },
          });
        }
      }
      setShowModal(false);
      setLanguageName("");
      setEditLanguage(null);
      await fetchLanguages();
    } catch (err) {
      console.error("Unexpected error:", err);
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: "An unexpected error occurred",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        toast: true,
        background: "white",
        customClass: {
          popup: "shadow-xl rounded-xl p-3",
        },
      });
    }
    setLoading(false);
  };

  const copyToClipboard = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDeleteLanguage = async (id) => {
    try {
      const { data: languageData, error: langError } = await supabase
        .from("languages")
        .select("languages, id")
        .eq("id", id)
        .single();

      if (langError || !languageData) {
        Swal.fire({
          position: "top-end",
          icon: "error",
          title: "Language not found",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          toast: true,
          background: "white",
          customClass: {
            popup: "shadow-xl rounded-xl p-3",
          },
        });
        return;
      }

      const { count, error: countError } = await supabase
        .from("tiles")
        .select("count", { count: "exact", head: true })
        .like("languages", `%${id}%`);

      const tileCount = count || 0;
      const shortId = languageData.id.slice(0, 10);

      const result = await Swal.fire({
        title: "Delete Language",
        html: `
          <div class="text-left">
            <p class="mb-2">Are you sure you want to delete <strong class="text-red-600">"${languageData.languages}"</strong>?</p>
            <p class="text-sm text-gray-500 mb-2">UUID: ${shortId}</p>
            <p class="text-sm text-orange-600">⚠️ This language is used in ${tileCount} tile${tileCount !== 1 ? "s" : ""}</p>
          </div>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#dc2626",
        background: "white",
        customClass: {
          popup: "shadow-xl rounded-xl p-3",
          confirmButton: "rounded-lg px-4 py-2",
          cancelButton: "rounded-lg px-4 py-2",
        },
      });

      if (!result.isConfirmed) return;

      // Cleanup tiles
      const { data: tiles, error: tilesErr } = await supabase
        .from("tiles")
        .select("id, languages")
        .like("languages", `%${id}%`);

      if (!tilesErr && tiles) {
        for (const tile of tiles) {
          let parsed = [];
          try {
            parsed = JSON.parse(tile.languages || "[]");
          } catch {
            parsed = [];
          }
          const newLangs = parsed.filter((l) => l !== id);
          await supabase
            .from("tiles")
            .update({ languages: JSON.stringify(newLangs) })
            .eq("id", tile.id);
        }
      }

      const { error } = await supabase.from("languages").delete().eq("id", id);
      if (error) {
        Swal.fire({
          position: "top-end",
          icon: "error",
          title: "Failed to delete language",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          toast: true,
          background: "white",
          customClass: {
            popup: "shadow-xl rounded-xl p-3",
          },
        });
      } else {
        setLanguages((prev) => prev.filter((item) => item.id !== id));
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Language deleted successfully",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          toast: true,
          background: "white",
          customClass: {
            popup: "shadow-xl rounded-xl p-3",
          },
        });
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: "An unexpected error occurred",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        toast: true,
        background: "white",
        customClass: {
          popup: "shadow-xl rounded-xl p-3",
        },
      });
    }
  };

  useEffect(() => {
    if (showModal && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showModal]);

  useEffect(() => {
    setPage(0);
    setHasMore(true);
    setLanguages([]);
    setIsLoading(true);
    fetchLanguages(0, true);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchLanguages();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const el = containerRef.current;
      if (!el || loadingMore || !hasMore) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchLanguages(nextPage);
      }
    };

    const el = containerRef.current;
    if (el) el.addEventListener("scroll", handleScroll);
    return () => {
      if (el) el.removeEventListener("scroll", handleScroll);
    };
  }, [page, hasMore, loadingMore]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12,
      },
    },
  };

  return (
    <div className="h-screen bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
      {/* Animated Background Pattern */}
      <div className="fixed inset-0 opacity-5 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(0,0,0,0.1) 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="h-full flex flex-col p-6 relative">
        {/* Header Section with Animation */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-6"
        >
          <div className="flex flex-wrap justify-between items-center gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                Language Management
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                Manage and organize your supported languages
              </p>
            </div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex-shrink-0"
            >
              <Button
                variant="primary"
                onClick={() => navigate("/languages/create")}
                className="px-5 py-2.5 text-sm flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <IoMdAdd className="text-lg" />
                Add Language
              </Button>
            </motion.div>
          </div>
        </motion.div>

        {/* Search Bar with Animation */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-6"
        >
          <div className="relative">
            <IoMdSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
            <SearchBar
              placeholder="Search languages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              inputClassName="pl-12 py-3 border-2 border-gray-200 focus:border-blue-400 rounded-xl transition-all duration-300"
            />
          </div>
        </motion.div>

        {/* Languages Container */}
        <motion.div
          ref={containerRef}
          className="flex-1 overflow-auto rounded-xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Header Stats */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FiGlobe className="text-blue-600 text-xl" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-800">
                      All Languages
                    </h2>
                   
                  </div>
                </div>
                {searchTerm && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                  >
                    Search: "{searchTerm}"
                  </motion.div>
                )}
              </div>
            </div>

            {/* Content */}
            {isLoading ? (
              <div className="p-6">
                <ListPageSkeleton count={6} />
              </div>
            ) : languages.length === 0 ? (
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center py-20"
              >
                <div className="text-6xl mb-4">🌐</div>
                <p className="text-gray-500 text-lg font-medium">
                  No languages found
                </p>
                <p className="text-gray-400 text-sm mt-2">
                  Click the "Add Language" button to create your first language
                </p>
              </motion.div>
            ) : (
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="divide-y divide-gray-100"
              >
                <AnimatePresence>
                  {languages.map((item) => (
                    <motion.div
                      key={item.id}
                      variants={itemVariants}
                      layout
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      whileHover={{ scale: 1.01 }}
                      transition={{ duration: 0.2 }}
                      onHoverStart={() => setHoveredCard(item.id)}
                      onHoverEnd={() => setHoveredCard(null)}
                      className="group relative bg-white hover:bg-gradient-to-r hover:from-purple-50/30 hover:to-transparent transition-all duration-300 cursor-pointer"
                      onClick={(e) => {
                        const isButton = e.target.closest("button");
                        if (!isButton) {
                          navigate(
                            `/tile?languages=${encodeURIComponent(item.id)}`,
                          );
                        }
                      }}
                    >
                      {/* Animated border on hover */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-purple-500/0 via-purple-500/0 to-purple-500/0 pointer-events-none"
                        animate={{
                          background:
                            hoveredCard === item.id
                              ? "linear-gradient(90deg, rgba(147,51,234,0) 0%, rgba(147,51,234,0.05) 50%, rgba(147,51,234,0) 100%)"
                              : "linear-gradient(90deg, rgba(147,51,234,0) 0%, rgba(147,51,234,0) 100%)",
                        }}
                        transition={{ duration: 0.3 }}
                      />

                      <div className="relative px-6 py-4">
                        <div className="flex justify-between items-center gap-4">
                          <div className="flex-1 min-w-0">
                            {/* Language Name with Animation */}
                            <div className="flex items-center gap-3 mb-2">
                             
                                <FiCode className="text-blue-600 text-xl" />
                              <motion.h3
                                className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2"
                                animate={{
                                  color:
                                    hoveredCard === item.id
                                      ? "#2563eb"
                                      : "#1f2937",
                                }}
                              >
                                {item.languages}
                              </motion.h3>
                              <motion.span
                                initial={{ scale: 0 }}
                                animate={{
                                  scale: hoveredCard === item.id ? 1 : 0,
                                }}
                                className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full"
                              >
                                Click to view tiles
                              </motion.span>
                            </div>

                            {/* ID Section with Copy */}
                            <div className="flex items-center gap-2 mt-2">
                              <FiDatabase className="text-gray-400 text-sm" />
                              <span className="text-xs text-gray-400 font-mono">
                                ID: {item.id.slice(0, 8)}...
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(item.id, `id-${item.id}`);
                                }}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                              >
                                {copiedId === `id-${item.id}` ? (
                                  <MdCheckCircle className="text-green-500" />
                                ) : (
                                  <MdContentCopy className="text-sm" />
                                )}
                              </button>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 flex-shrink-0">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => navigate(`/languages/${item.id}`)}
                              className="p-2 bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100 transition-all duration-300"
                            >
                              <MdModeEditOutline className="text-lg" />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleDeleteLanguage(item.id)}
                              className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all duration-300"
                            >
                              <MdDelete className="text-lg" />
                            </motion.button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Loading More Indicator */}
                {loadingMore && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-6"
                  >
                    <div className="inline-flex items-center gap-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-purple-500 border-t-transparent"></div>
                      <p className="text-gray-500 text-sm">
                        Loading more languages...
                      </p>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Premium Modal */}
      <Modal
        customClasses="w-full sm:max-w-lg md:max-w-2xl"
        showModal={showModal}
        setShowModal={setShowModal}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative bg-white rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Modal Header Gradient */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-red-500"></div>

          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <motion.h2
                className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent"
                initial={{ x: -20 }}
                animate={{ x: 0 }}
              >
                {editLanguage ? "Edit Language" : "Add New Language"}
              </motion.h2>
              <motion.button
                whileHover={{ rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <IoMdClose className="text-2xl" />
              </motion.button>
            </div>

            <motion.div
              className="space-y-4"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <div className="group">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Language Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FiGlobe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
                  <input
                    type="text"
                    placeholder="e.g., English, Spanish, French"
                    value={languageName}
                    onChange={(e) => setLanguageName(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-gray-700 placeholder-gray-400 focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all duration-300"
                    ref={inputRef}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Enter the language name as it should appear in the system
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all duration-300"
                >
                  Cancel
                </motion.button>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="primary"
                    onClick={handleAddOrUpdateLanguage}
                    disabled={loading}
                    className="px-6 py-2 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Saving...
                      </div>
                    ) : editLanguage ? (
                      "Update Language"
                    ) : (
                      "Create Language"
                    )}
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </Modal>
    </div>
  );
}

export default Languages;
